import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, useColorModeValue, useColorMode, Flex } from '@chakra-ui/react';
import { DateRangePicker, defaultStaticRanges, createStaticRanges } from 'react-date-range';
import moment from 'moment';
import { toZonedTime } from "date-fns-tz";
import { CalendarIcon, ChevronDownIcon } from '@chakra-ui/icons';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const START_DATE = moment().subtract(36, 'months').toDate();  // 36 months older from current date

const staticRanges = [
    ...defaultStaticRanges,
    ...createStaticRanges([
        {
            label: 'Last 7 days',
            range: () => {
                const sevenDaysAgo = moment().subtract(7, 'days').toDate();
                return {
                    startDate: sevenDaysAgo,
                    endDate: new Date()
                };
            }
        },
        {
            label: 'Last 14 days',
            range: () => {
                const fourteenDaysAgo = moment().subtract(14, 'days').toDate();
                return {
                    startDate: fourteenDaysAgo,
                    endDate: new Date()
                };
            }
        },
        {
            label: 'Last 30 days',
            range: () => {
                const thirtyDaysAgo = moment().subtract(30, 'days').toDate();
                return {
                    startDate: thirtyDaysAgo,
                    endDate: new Date()
                };
            }
        },
        {
            label: 'Maximum',
            range: () => ({
                startDate: START_DATE,
                endDate: new Date(),
            })
        },
    ])
];

const presetMap = {
    "this_week_sun_today": "This Week",
    "last_week_sun_sat": "Last Week",
    "last_7d": "Last 7 Days",
    "last_14d": "Last 14 Days",
    "last_30d": "Last 30 Days",
    "yesterday": "Yesterday",
    "today": "Today",
    "this_month": "This Month",
    "last_month": "Last Month",
    "maximum": "Maximum"
};

const DateRangeSelector = ({ onDateRangeChange, initialStartDate, initialEndDate, initialDatePreset, timeZone }) => {

    const inputBackground = useColorModeValue('white', 'inherit');
    const hoverColor = useColorModeValue("gray.50", "gray.800");
    const cancelButtonColor = useColorModeValue("gray", "gray.800");
    const updateButtonColor = useColorModeValue("brand", "gray");
    const { colorMode, toggleColorMode } = useColorMode();
    const [showDateRangePicker, setShowDateRangePicker] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [datePreset, setDatePreset] = useState('');
    const [tempStartDate, setTempStartDate] = useState(moment().subtract(7, 'days').toDate());
    const [tempEndDate, setTempEndDate] = useState(null);
    const maxDate = (toZonedTime(new Date(), timeZone));

    useEffect(() => {
        if (initialStartDate && initialEndDate) {
            setStartDate(initialStartDate);
            setEndDate(initialEndDate);
            setDatePreset(presetMap[initialDatePreset] || '');
            setTempStartDate(initialStartDate);
            setTempEndDate(initialEndDate);
        }
    }, [initialStartDate, initialEndDate, initialDatePreset]);

    // Ref for the date picker container
    const datePickerRef = useRef(null);
    const buttonRef = useRef(null);

    // Close the picker when clicking outside of it
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if the click is outside of the date range picker or the button
            if (datePickerRef.current && !datePickerRef.current.contains(event.target) && !buttonRef.current.contains(event.target)) {
                setShowDateRangePicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDateSelect = (ranges) => {
        const { startDate, endDate } = ranges.selection;
        setTempStartDate(startDate);
        setTempEndDate(endDate);
    };

    const handleCancel = () => {
        setTempStartDate(startDate); // Reset to original start date
        setTempEndDate(endDate); // Reset to original end date
        setShowDateRangePicker(false); // Close the picker
    };

    const handleUpdate = () => {

        let updatedStartDate = tempStartDate;
        let updatedEndDate = tempEndDate;

        // Ensure that the startDate and endDate do not exceed maxDate
        if (updatedEndDate > maxDate) {
            updatedEndDate = maxDate;
        }
        if (updatedStartDate > maxDate) {
            updatedStartDate = maxDate;
        }

        setStartDate(updatedStartDate);
        setEndDate(updatedEndDate);

        const start = moment(updatedStartDate).startOf('day');
        const end = moment(updatedEndDate).startOf('day');

        // Check for "Today"
        if (start.isSame(moment().startOf('day'), 'day') && end.isSame(moment().endOf('day'), 'day')) {
            setDatePreset("Today");
            onDateRangeChange(updatedStartDate, updatedEndDate, "today");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Yesterday"
        if (start.isSame(moment().subtract(1, 'days').startOf('day'), 'day') &&
            end.isSame(moment().subtract(1, 'days').endOf('day'), 'day')) {
            setDatePreset("Yesterday");
            onDateRangeChange(updatedStartDate, updatedEndDate, "yesterday");
            setShowDateRangePicker(false);
            return;
        }

        const startOfWeek = moment().startOf('week').startOf('day'); // Start of the current week (Sunday)
        const endOfWeek = moment().endOf('day'); // End of the current day

        if (start.isSameOrAfter(startOfWeek) && end.isSameOrBefore(endOfWeek)) {
            setDatePreset("This Week");
            onDateRangeChange(updatedStartDate, updatedEndDate, "this_week_sun_today");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Last Week"
        const lastWeekStart = moment().subtract(1, 'week').startOf('week').startOf('day');
        const lastWeekEnd = moment().subtract(1, 'week').endOf('week').endOf('day');
        if (start.isSame(lastWeekStart, 'day') && end.isSame(lastWeekEnd, 'day')) {
            setDatePreset("Last Week");
            onDateRangeChange(updatedStartDate, updatedEndDate, "last_week_sun_sat");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Last Month"
        const startOfLastMonth = moment().subtract(1, 'month').startOf('month').startOf('day');
        const endOfLastMonth = moment().subtract(1, 'month').endOf('month').endOf('day');
        if (start.isSame(startOfLastMonth, 'day') && end.isSame(endOfLastMonth, 'day')) {
            setDatePreset("Last Month");
            onDateRangeChange(updatedStartDate, updatedEndDate, "last_month");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Last 7 Days"
        if (start.isSame(moment().subtract(7, 'days').startOf('day'), 'day') &&
            end.isSame(moment().endOf('day'), 'day')) {
            setDatePreset("Last 7 Days");
            onDateRangeChange(updatedStartDate, updatedEndDate, "last_7d");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Last 14 Days"
        if (start.isSame(moment().subtract(14, 'days').startOf('day'), 'day') &&
            end.isSame(moment().endOf('day'), 'day')) {
            setDatePreset("Last 14 Days");
            onDateRangeChange(updatedStartDate, updatedEndDate, "last_14d");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Last 30 Days"
        if (start.isSame(moment().subtract(30, 'days').startOf('day'), 'day') &&
            end.isSame(moment().endOf('day'), 'day')) {
            setDatePreset("Last 30 Days");
            onDateRangeChange(updatedStartDate, updatedEndDate, "last_30d");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "This Month"
        const startOfMonth = moment().startOf('month').startOf('day');
        const endOfMonth = moment().endOf('day');
        if (start.isSameOrAfter(startOfMonth) && end.isSameOrBefore(endOfMonth)) {
            setDatePreset("This Month");
            onDateRangeChange(updatedStartDate, updatedEndDate, "this_month");
            setShowDateRangePicker(false);
            return;
        }

        // Check for "Maximum" range
        if (start.isSame(START_DATE, 'day') && end.isSame(maxDate, 'day')) {
            setDatePreset("Maximum");
            onDateRangeChange(updatedStartDate, updatedEndDate, "maximum");
            setShowDateRangePicker(false);
            return;
        }

        setDatePreset("");
        onDateRangeChange(updatedStartDate, updatedEndDate, "");
        setShowDateRangePicker(false);
    };


    const formatDateRange = (startDate, endDate) => {
        // If preset is 'Today' or 'Yesterday', only show startDate
        return `${moment(startDate).format('YYYY-MM-DD')}${(datePreset === "Today" || datePreset === "Yesterday") ? '' : ` - ${moment(endDate).format('YYYY-MM-DD')}`}`;
    };

    const getTimezone = () => {
        if (timeZone) {
            const parts = timeZone.split('/');
            if (parts.length > 1) {
                return `Dates are shown in ${parts[1].replace('_', ' ')} Time`;
            }
            return `Dates are shown in ${timeZone.replace('_', ' ').replace('/', ' ')} Time`;
        }
        const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return `Dates are shown in ${currentTimeZone.replace('_', ' ').replace('/', ' ')} Time`;
    };

    const buttonText = datePreset
        ? `${datePreset}: ${formatDateRange(startDate, endDate)}`
        : formatDateRange(startDate, endDate);

    return (
        <Box>
            <Button
                ref={buttonRef}
                size="sm"
                //bg={inputBackground}
                colorScheme='brand'
                fontWeight={'normal'}
                borderRadius={'5px'}
                // border={'1px'}
                // borderColor="gray.200"
                // _hover={{
                //     borderColor: 'gray.500',
                //     bg: hoverColor
                // }}
                leftIcon={<CalendarIcon />}
                onClick={() => setShowDateRangePicker(prevState => !prevState)}
                rightIcon={<ChevronDownIcon boxSize={5} />}
            >
                {buttonText || "Select Date Range"}
            </Button>
            {showDateRangePicker && (
                <Box
                    ref={datePickerRef}
                    position="absolute"
                    zIndex={99999}
                    mt={4}
                    right={5}
                    bg={'#fff'}
                >
                    <Box
                        boxShadow="0 4px 8px rgba(0, 0, 0, 0.1)"
                        border="1px solid #ccc"
                        borderRadius="2px"
                        className={colorMode == 'dark' ? 'dark-mode' : 'light-mode'}
                    >
                        <DateRangePicker
                            ranges={[{
                                startDate: tempStartDate,
                                endDate: tempEndDate,
                                key: 'selection',
                            }]}
                            onChange={handleDateSelect}
                            showMonthAndYearPickers={true}
                            months={2}
                            maxDate={maxDate}
                            direction="horizontal"
                            staticRanges={staticRanges}
                            rangeColors={["#40225f"]}
                            inputRanges={[]}
                            style={{
                                width: '250px',
                                height: 'auto',
                            }}
                        />
                        <Box display="flex" justifyContent="right" p={2} mt={-8}>
                            <Flex flex={0.7} color={'gray.800'} fontSize="12px" pt={2}>{getTimezone()}</Flex>
                            <Flex gap={2}>
                                <Button variant={'solid'} onClick={handleCancel} colorScheme={cancelButtonColor} bg={'gray.100'} size="sm">Cancel</Button>
                                <Button onClick={handleUpdate} colorScheme={updateButtonColor} bg={'brand.500'} size="sm">Update</Button>
                            </Flex>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DateRangeSelector;